import { ReactNode, useRef } from 'react';

import clsxm from '@/lib/clsxm';
import { trackModuleViewed } from '@/lib/tracking';
import { Tracking } from '@/lib/types';
import useBreakpoints from '@/hooks/use-breakpoints';
import { useGlobalContext } from '@/hooks/use-global-context';
import useMonitorVisibility from '@/hooks/use-monitor-visibility';

import { Background, backgroundMap } from '@/components/background/background';
import CTA, { CTAProps } from '@/components/cta';
import { HygraphImageProps } from '@/components/hygraph-image';
import Row from '@/components/row';
import Separator, { SeparatorProps } from '@/components/separator';
import Typography from '@/components/typography';

import { SectionContextProvider } from '@/context/section-context';
import { BackgroundWidth } from '@/enums/background-width';
import { LightColor } from '@/enums/light-color';
import { RowLayout } from '@/enums/row-layout';
import { Spacing } from '@/enums/spacing';

export type SectionProps = {
  index?: number;
  slug?: string;
  backgroundColor?: LightColor;
  backgroundImage?: HygraphImageProps;
  backgroundImageMobile?: HygraphImageProps;
  backgroundWidth?: BackgroundWidth;
  children?: ReactNode;
  separatorAfter?: SeparatorProps;
  sectionTracking?: Tracking;
  title?: string;
  heading?: string;
  caption?: string;
  overlappingCTA?: CTAProps;
};

export default function Section(props: SectionProps) {
  const {
    slug,
    backgroundColor,
    backgroundImage,
    backgroundImageMobile,
    backgroundWidth,
    children,
    separatorAfter,
    sectionTracking,
    title,
    heading,
    caption,
    overlappingCTA,
  } = props;

  const isHidden = title?.includes('AB Test');
  const hasHeading = !!heading || !!caption;

  const { currentEntry: currentBackgroundImage } = useBreakpoints({
    md: backgroundImageMobile || backgroundImage || null,
    lg: backgroundImage || null,
  });

  const containerRef = useRef(null);

  const { pageMeta } = useGlobalContext();

  const [ref] = useMonitorVisibility({
    triggerOnce: () => {
      trackModuleViewed({
        pageMeta,
        trackingValues: sectionTracking,
      });
    },
  });

  const trackingEnabled =
    pageMeta.templateSetting?.enableSegmentTracking && sectionTracking;

  return (
    <SectionContextProvider sectionProps={props}>
      <section
        id={slug}
        className={clsxm('relative', isHidden && 'hidden')}
        ref={containerRef}
        style={{
          backgroundImage: currentBackgroundImage
            ? `url('${currentBackgroundImage.url}')`
            : undefined,
        }}
      >
        {trackingEnabled && <div ref={ref} />}
        <Background
          backgroundColor={backgroundColor}
          backgroundWidth={backgroundWidth}
        />
        <div
          className={clsxm(
            'z-2 relative mx-auto w-full',
            backgroundColor && ((!overlappingCTA && 'pb-12 pt-10') || 'pb-20'),
            hasHeading && 'pt-10'
          )}
        >
          {hasHeading && (
            <Row layout={RowLayout.MAX_WIDTH_CENTER_WIDE}>
              <div className='text-center'>
                {!!caption && (
                  <>
                    <Typography.Eyebrow as='h2' className='mb-3'>
                      {caption}
                    </Typography.Eyebrow>
                  </>
                )}

                {!!heading && (
                  <>
                    <Typography.Heading1 as='h3'>{heading}</Typography.Heading1>
                    <Separator spacing={Spacing.MEDIUM} />
                  </>
                )}
              </div>
            </Row>
          )}
          {children}
        </div>
      </section>

      {overlappingCTA && (
        <div className='relative -translate-y-10 px-4'>
          {backgroundColor && (
            <div
              className={clsxm(
                'absolute left-0 top-0 z-0 h-1/2 w-full',
                backgroundMap[backgroundColor].main
              )}
            />
          )}
          <div className='relative z-20'>
            <CTA {...overlappingCTA} />
          </div>
        </div>
      )}
      {separatorAfter && <Separator {...separatorAfter} />}
    </SectionContextProvider>
  );
}
